import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import "../../../components/uploadFile/upload.css";

import { MdCloudUpload, MdDelete } from "react-icons/md";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import CircularProgressWithLabel from "./CircularProgressWithLabel";

/**
 * AlertDialog - Componente de diálogo para importar documentos
 */
export default function UploadInvoices({
  handleClose,
  open,
  origem,
  handleUpload,
  operationType,
  uploadProgress = 0,
}) {
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);

  const isCargaOperation = operationType === "carga";
  const allowMultipleFiles =
    isCargaOperation || origem === "Canhoto" || origem === "Invoices";

  const COLORS = useMemo(
    () => ({
      bg: "#F9FAFB",
      ink: "#012442",
      accent: "#ff3100",
      shadow: "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
      weight: 400,
    }),
    []
  );

  const getAllowedExtensions = () => {
    if (isCargaOperation) return ["pdf", "png", "jpeg", "jpg", "xml", "zip"];
    return ["png", "jpeg", "jpg"];
  };

  const isValidFileForOperation = (file) => {
    if (!file || !file.name) return false;
    const allowedExtensions = getAllowedExtensions();
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    return allowedExtensions.includes(extension);
  };

  const handleFileChange = ({ target: { files: selectedFiles } }) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const selectedFilesArray = Array.from(selectedFiles);
      const newFiles = selectedFilesArray.filter((file) =>
        isValidFileForOperation(file)
      );
      const invalidFilesCount = selectedFilesArray.length - newFiles.length;

      const allowedTypesMessage = isCargaOperation
        ? "pdf, png, jpeg, xml e zip"
        : "png e jpeg";

      if (newFiles.length === 0) {
        alert(
          `Tipo de arquivo inválido para esta operação. Tipos permitidos: ${allowedTypesMessage}.`
        );
        return;
      }

      if (invalidFilesCount > 0) {
        alert(
          `${invalidFilesCount} arquivo(s) foram ignorados por tipo inválido. Tipos permitidos: ${allowedTypesMessage}.`
        );
      }

      setFiles((prevFiles) => [...prevFiles, ...newFiles]);

      const newPreviews = newFiles.map((file) => ({
        url: URL.createObjectURL(file),
        name: "Documentação-" + file.name,
        type: file.type,
      }));

      setFilePreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
    setFilePreviews(
      filePreviews.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleUploadClick = async () => {
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const fileToUpload of files) {
        await handleUpload(fileToUpload, true);
      }
      cleanupState();
      handleClose();
    } catch (error) {
      console.log("Erro durante o upload de um dos arquivos:", error);
      setUploading(false);
    } finally {
      setUploading(false);
    }
  };

  const getAllowedFileTypes = () => {
    if (isCargaOperation) {
      return ".pdf,.png,.jpeg,.jpg,.xml,.zip,application/pdf,image/png,image/jpeg,application/xml,text/xml,application/zip,application/x-zip-compressed";
    } else if (operationType === "descarga") {
      return ".png,.jpeg,.jpg,image/png,image/jpeg";
    }
    return ".png,.jpeg,.jpg,image/png,image/jpeg";
  };

  const cleanupState = () => {
    setFiles([]);
    setFilePreviews([]);
  };

  const allowedLabel = useMemo(() => {
    const ext = getAllowedExtensions().map((e) => e.toUpperCase()).join(" • ");
    return `Tipos: ${ext}`;
  }, [operationType]);

  const getFileMeta = (fileName = "") => {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";

    if (ext === "pdf") return { icon: <DescriptionRoundedIcon />, tag: "PDF" };
    if (["png", "jpg", "jpeg"].includes(ext)) {
      return { icon: <ImageRoundedIcon />, tag: "Imagem" };
    }
    if (ext === "zip") return { icon: <ArchiveRoundedIcon />, tag: "ZIP" };
    if (ext === "xml") return { icon: <DataObjectRoundedIcon />, tag: "XML" };

    return {
      icon: <DescriptionRoundedIcon />,
      tag: ext.toUpperCase() || "ARQ",
    };
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        cleanupState();
        handleClose();
      }}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: COLORS.bg,
          boxShadow: COLORS.shadow,
          overflow: "hidden",
          width: "100%",
          maxWidth: "720px",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          color: COLORS.ink,
          fontWeight: 700,
          letterSpacing: "-0.2px",
        }}
      >
        Importar NF-s do Carregamento

        <Typography
          sx={{
            mt: 0.5,
            color: "rgba(1,36,66,0.75)",
            fontWeight: COLORS.weight,
            fontSize: 13,
          }}
        >
          Selecione arquivos válidos e confirme para enviar.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
          <Chip
            size="small"
            label={isCargaOperation ? "Operação: Carga" : "Operação: Descarga"}
            sx={{
              bgcolor: "rgba(1,36,66,0.08)",
              color: COLORS.ink,
              fontWeight: 600,
            }}
          />

          <Chip
            size="small"
            label={allowedLabel}
            sx={{
              bgcolor: "rgba(255,49,0,0.10)",
              color: COLORS.ink,
              fontWeight: 600,
            }}
          />
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => document.querySelector(".input-field")?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              document.querySelector(".input-field")?.click();
            }
          }}
          sx={{
            borderRadius: 3,
            p: 2.5,
            bgcolor: "#fff",
            border: "1px dashed rgba(1,36,66,0.25)",
            boxShadow: "0px 1px 0px rgba(0,14,51,0.08)",
            transition: "all .15s ease",
            cursor: "pointer",
            width: "100%",
            "&:hover": {
              borderColor: "rgba(1,36,66,0.45)",
              boxShadow: "0px 8px 20px rgba(0,14,51,0.12)",
              transform: "translateY(-1px)",
            },
          }}
        >
          <input
            type="file"
            accept={getAllowedFileTypes()}
            className="input-field"
            hidden
            multiple={allowMultipleFiles}
            onChange={handleFileChange}
          />

          {filePreviews.length === 0 ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  bgcolor: "rgba(1,36,66,0.08)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <MdCloudUpload color={COLORS.ink} size={30} />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: COLORS.ink,
                    fontWeight: 700,
                    fontSize: 15,
                    letterSpacing: "-0.1px",
                  }}
                >
                  Clique para selecionar ou arraste arquivos aqui
                </Typography>

                <Typography
                  sx={{
                    mt: 0.3,
                    color: "rgba(1,36,66,0.70)",
                    fontWeight: COLORS.weight,
                    fontSize: 13,
                  }}
                >
                  {allowMultipleFiles
                    ? "Você pode enviar múltiplos arquivos."
                    : "Envie apenas imagens (PNG/JPG)."}
                </Typography>
              </Box>

             
            </Stack>
          ) : (
            <Box sx={{ width: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5, gap: 1, flexWrap: "wrap" }}
              >
                <Typography
                  sx={{
                    color: COLORS.ink,
                    fontWeight: 800,
                    fontSize: 14,
                    letterSpacing: "-0.1px",
                  }}
                >
                  Arquivos selecionados ({filePreviews.length})
                </Typography>

                <Chip
                  size="small"
                  label={uploading ? "Enviando..." : "Pronto para importar"}
                  sx={{
                    bgcolor: uploading
                      ? "rgba(1,36,66,0.12)"
                      : "rgba(255,49,0,0.10)",
                    color: COLORS.ink,
                    fontWeight: 700,
                  }}
                />
              </Stack>

              <Box sx={{ maxHeight: 280, overflowY: "auto", pr: 0.5 }}>
                <List disablePadding>
                  {filePreviews.map((file, index) => {
                    const meta = getFileMeta(file.name);

                    return (
                      <ListItem
                        key={index}
                        disableGutters
                        sx={{
                          mb: 1,
                          px: 1.5,
                          py: 1,
                          pr: 7,
                          borderRadius: 2,
                          bgcolor: "#fff",
                          border: "1px solid rgba(1,36,66,0.10)",
                          boxShadow: "0px 1px 0px rgba(0,14,51,0.06)",
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                            sx={{
                              color: COLORS.accent,
                              right: 8,
                              "&:hover": {
                                bgcolor: "rgba(255,49,0,0.10)",
                              },
                            }}
                          >
                            <MdDelete size={20} />
                          </IconButton>
                        }
                      >
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                          sx={{ width: "100%", pr: 6 }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor: "rgba(1,36,66,0.08)",
                              display: "grid",
                              placeItems: "center",
                              color: COLORS.ink,
                              flex: "0 0 auto",
                            }}
                          >
                            {meta.icon}
                          </Box>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              noWrap
                              sx={{
                                color: COLORS.ink,
                                fontWeight: 700,
                                fontSize: 13.5,
                              }}
                            >
                              {file.name}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.2,
                                color: "rgba(1,36,66,0.70)",
                                fontWeight: COLORS.weight,
                                fontSize: 12.5,
                              }}
                            >
                              Clique no ícone para remover
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={meta.tag}
                            sx={{
                              bgcolor: "rgba(1,36,66,0.08)",
                              color: COLORS.ink,
                              fontWeight: 800,
                              flexShrink: 0,
                            }}
                          />
                        </Stack>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mt: 2.5, display: "flex", justifyContent: "center" }}>
              <CircularProgressWithLabel value={uploadProgress} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={handleUploadClick}
          disabled={files.length === 0 || uploading}
          variant="contained"
          sx={{
            bgcolor: COLORS.ink,
            color: "#fff",
            fontWeight: 800,
            textTransform: "none",
            borderRadius: 3,
            px: 2.25,
            py: 1.1,
            boxShadow: COLORS.shadow,
            "&:hover": { bgcolor: "#011B30" },
            "&:disabled": {
              bgcolor: "rgba(1,36,66,0.25)",
              color: "rgba(255,255,255,0.9)",
            },
          }}
        >
          {uploading
            ? `Importando ${files.length} arquivo(s)...`
            : `Importar ${files.length} arquivo(s)`}
        </Button>

        <Button
          onClick={() => {
            cleanupState();
            handleClose();
          }}
          variant="outlined"
          sx={{
            ml: 1,
            borderColor: "rgba(255,49,0,0.55)",
            color: COLORS.accent,
            fontWeight: 800,
            textTransform: "none",
            borderRadius: 3,
            px: 2.1,
            py: 1.1,
            // "&:hover": {
            //   borderColor: COLORS.accent,
            //   bgcolor: "rgba(255,49,0,0.08)",
            // },
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
